import React, { useState, useMemo, useEffect, useRef } from 'react';
import { TylerState, updateReact, useTylerStateUpdate } from '../../utils/TylerState';
import {
  MonthlyProjection,
  Currency,
  CURRENCY_SYMBOLS,
  CURRENCIES,
  FlowPeriod,
  YearSummary,
  MonthlyAccountProjection,
} from '../../types/personalForecast';
import { generateForecast, aggregateFlowsByCategory, summarizeByYear, buildIncomeStatement, otherBalanceChange, getFXRate } from '../../utils/forecastEngine';
import { buildInsights } from '../../utils/forecastInsights';
import { buildRetirementHandoff, stashHandoff } from '../../utils/forecastToRetirement';

type ResultsView = 'dashboard' | 'cashFlow' | 'balanceSheet' | 'accountDetail' | 'incomeStatement';

/** One column of every table: a month or a calendar year. Time always runs across the top. */
interface PeriodColumn {
  key: string;
  label: string;
  source: 'actual' | 'forecast' | 'mixed';
  /** Index into the monthly projections used for real-dollar deflation. */
  index: number;
  monthly?: MonthlyProjection;
  yearly?: YearSummary;
}

const monthLabel = (month: string) =>
  new Date(month + '-01T00:00:00').toLocaleDateString('en-CA', { month: 'short', year: '2-digit' });

/**
 * Green for a figure that helped you, red for one that cost you - decided by the figure itself,
 * never by which row it sits on. A recorded inflow can be negative (a withdrawal keyed against a
 * contribution), and tax that refunds on a loss is an inflow that usually costs, so a row-level
 * colour will sooner or later print a green minus sign.
 */
export const hueOf = (v: number) => (v >= 0 ? '#059669' : '#dc2626');

const ForecastResults: React.FC = () => {
  useTylerStateUpdate();
  const { accounts, cashFlows, fxRates, settings } = TylerState.forecast;
  const [view, setView] = useState<ResultsView>('dashboard');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  // Reporting currency by default: a column mixing GBP and CAD rows cannot be read down.
  const [showLocalCurrency, setShowLocalCurrency] = useState(false);
  const [showDismissedInsights, setShowDismissedInsights] = useState(false);

  const projections = useMemo(() => {
    if (accounts.length === 0) return [];
    return generateForecast(accounts, cashFlows, fxRates, settings);
  }, [accounts, cashFlows, fxRates, settings]);

  // Long horizons open in Annual; the switch is always there.
  const [period, setPeriod] = useState<FlowPeriod>(() => (projections.length > 120 ? 'year' : 'month'));

  if (projections.length > 0) {
    TylerState.forecast.projections = projections;
  }

  const years = useMemo(() => summarizeByYear(projections), [projections]);
  const flowTable = useMemo(
    () => aggregateFlowsByCategory(projections, cashFlows, accounts, fxRates, settings, period),
    [projections, cashFlows, accounts, fxRates, settings, period]
  );
  // Insights always read the annual statement, whatever the view is toggled to - a rule about a
  // year should not change its answer because someone switched the table to months.
  const incomeStatementYearly = useMemo(
    () => buildIncomeStatement(projections, years, accounts, fxRates, settings, 'year'),
    [projections, years, accounts, fxRates, settings]
  );
  const insights = useMemo(
    () => buildInsights(projections, years, accounts, cashFlows, settings, incomeStatementYearly),
    [projections, years, accounts, cashFlows, settings, incomeStatementYearly],
  );

  const incomeStatement = useMemo(
    () => buildIncomeStatement(projections, years, accounts, fxRates, settings, period),
    [projections, years, accounts, fxRates, settings, period]
  );

  const columns: PeriodColumn[] = useMemo(() =>
    period === 'month'
      ? projections.map((p, i) => ({ key: p.month, label: monthLabel(p.month), source: p.source, index: i, monthly: p }))
      : years.map(y => ({ key: y.year, label: y.year, source: y.source, index: y.lastIndex, yearly: y })),
  [period, projections, years]);

  const firstForecastIdx = Math.max(0, projections.findIndex(p => p.source === 'forecast'));
  const hasHistory = projections.some(p => p.source === 'actual');
  const nowIdx = hasHistory ? Math.max(0, firstForecastIdx - 1) : 0;

  // Every table opens at today: the current month (or year) sits right after the frozen label column.
  const rootRef = useRef<HTMLDivElement>(null);
  const currentKey = projections[nowIdx]?.month.slice(0, period === 'month' ? 7 : 4);
  useEffect(() => {
    rootRef.current?.querySelectorAll<HTMLElement>('.frozen-table-wrap').forEach(wrap => {
      const current = wrap.querySelector<HTMLElement>(`th[data-period="${currentKey}"]`);
      const label = wrap.querySelector<HTMLElement>('th');
      if (!current || !label) return;
      const firstVisibleEdge = wrap.getBoundingClientRect().left + label.getBoundingClientRect().width;
      wrap.scrollLeft += current.getBoundingClientRect().left - firstVisibleEdge;
    });
  }, [view, period, selectedAccountId, currentKey]);

  const deflate = (nominal: number, monthIndex: number): number => {
    if (settings.valuationMode !== 'real') return nominal;
    const yearsOut = Math.max(0, monthIndex - firstForecastIdx) / 12;
    return nominal / Math.pow(1 + settings.inflationRate / 100, yearsOut);
  };

  const fmt = (amount: number, currency: Currency = settings.baseCurrency) => {
    const sym = CURRENCY_SYMBOLS[currency];
    const abs = Math.abs(amount);
    const formatted = abs.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return amount < 0 ? `-${sym}${formatted}` : `${sym}${formatted}`;
  };

  if (accounts.length === 0) {
    return (
      <div style={{ padding: '2rem', color: '#94a3b8', textAlign: 'center' }}>
        <h2>Forecast</h2>
        <p>Add accounts and cash flows first to generate projections.</p>
      </div>
    );
  }

  if (projections.length === 0) return null;

  const first = projections[nowIdx];
  const last = projections[projections.length - 1];
  const lastIdx = projections.length - 1;

  // ---- shared table pieces: periods across the top, one line item per row ----
  const sourceColor = (s: PeriodColumn['source']) => (s === 'actual' ? '#059669' : s === 'mixed' ? '#b45309' : '#94a3b8');
  // Cash means current accounts, so name them: the line should say where the money went rather
  // than leaving the reader to guess. No notion of a "primary" account exists to pick one by, and
  // picking the biggest would quietly change as balances move.
  const currentAccounts = accounts.filter(a => a.bucket === 'current');
  const bankLabel = currentAccounts.length === 0 ? 'To your bank'
    : currentAccounts.length <= 2
      ? `To ${currentAccounts.map(a => (a.name || 'unnamed').replace(/\s*\(.*?\)\s*/, ' ').trim()).join(', ')}`
      : `To your ${currentAccounts.length} bank accounts`;

  // Calendar-year age: the age reached at some point during that year, which is how you'd naturally
  // say "I'll go part time at 60" without pinning it to a birthday.
  const ageIn = (periodKey: string) =>
    settings.birthYear ? Number(periodKey.slice(0, 4)) - settings.birthYear : null;

  const headerRow = (firstLabel: string) => (
    <thead>
      <tr>
        <th>{firstLabel}</th>
        {columns.map(c => (
          <th key={c.key} data-period={c.key} style={{ textAlign: 'right', whiteSpace: 'nowrap' }} title={c.yearly && c.yearly.months < 12 ? `${c.yearly.months} months` : undefined}>
            {c.label}
            {ageIn(c.key) !== null && (
              <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#94a3b8' }}>age {ageIn(c.key)}</div>
            )}
            {hasHistory && (
              <div style={{ fontSize: '0.65rem', fontWeight: 500, textTransform: 'uppercase', color: sourceColor(c.source) }}>
                {c.source === 'mixed' ? 'part actual' : c.source}
              </div>
            )}
            {c.yearly && c.yearly.months < 12 && <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#94a3b8' }}>{c.yearly.months} mo</div>}
          </th>
        ))}
      </tr>
    </thead>
  );
  const cell = (value: number | undefined, opts: { color?: string; bold?: boolean; currency?: Currency; dash?: boolean } = {}) => (
    <td style={{ textAlign: 'right', color: opts.color, fontWeight: opts.bold ? 700 : undefined, whiteSpace: 'nowrap' }}>
      {value === undefined || (opts.dash && !value) ? '-' : fmt(value, opts.currency)}
    </td>
  );
  // The label sits in the frozen first column so it stays put when the periods scroll.
  const sectionRow = (label: string, color?: string) => (
    <tr><td title={label} style={{ fontWeight: 700, color, background: '#f8fafc' }}>{label}</td><td colSpan={columns.length} style={{ background: '#f8fafc' }} /></tr>
  );
  const accountAt = (c: PeriodColumn, id: string): MonthlyAccountProjection | undefined =>
    c.monthly ? c.monthly.accounts[id] : c.yearly?.accounts[id];
  const totalsAt = (c: PeriodColumn) => (c.monthly || c.yearly)!;

  const periodSwitch = (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '0.85rem' }}>
      {(['month', 'year'] as FlowPeriod[]).map(p => (
        <button key={p} className={period === p ? 'btn-primary' : 'btn-secondary'} onClick={() => setPeriod(p)} style={{ padding: '4px 10px', fontSize: '0.85rem' }}>
          {p === 'month' ? 'Monthly' : 'Annual'}
        </button>
      ))}
    </div>
  );

  return (
    <div ref={rootRef}>
      {/* Controls: view, period, valuation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {(['dashboard', 'cashFlow', 'balanceSheet', 'incomeStatement', 'accountDetail'] as ResultsView[]).map(v => (
            <button
              key={v}
              className={view === v ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setView(v)}
              style={{ padding: '6px 14px', fontSize: '0.9rem' }}
            >
              {v === 'dashboard' ? 'Dashboard' : v === 'cashFlow' ? 'Cash Flow' : v === 'balanceSheet' ? 'Balance Sheet' : v === 'incomeStatement' ? 'Income Statement' : 'Account Detail'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
          {periodSwitch}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '0.85rem' }}>
            <button
              className={settings.valuationMode === 'nominal' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => { settings.valuationMode = 'nominal'; updateReact(); }}
              style={{ padding: '4px 10px', fontSize: '0.85rem' }}
            >
              Nominal $
            </button>
            <button
              className={settings.valuationMode === 'real' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => { settings.valuationMode = 'real'; updateReact(); }}
              style={{ padding: '4px 10px', fontSize: '0.85rem' }}
            >
              Real $
            </button>
          </div>
          {accounts.some(a => a.currency !== settings.baseCurrency) && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '0.85rem' }}>
              <button
                className={!showLocalCurrency ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setShowLocalCurrency(false)}
                title="Show every account converted, so a column adds up"
                style={{ padding: '4px 10px', fontSize: '0.85rem' }}
              >
                All {settings.baseCurrency}
              </button>
              <button
                className={showLocalCurrency ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setShowLocalCurrency(true)}
                title="Show each account in the currency it is actually held in"
                style={{ padding: '4px 10px', fontSize: '0.85rem' }}
              >
                Local
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dashboard */}
      {view === 'dashboard' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <button
              className="btn-secondary"
              onClick={() => {
                const handoff = buildRetirementHandoff(accounts, cashFlows, settings, projections);
                stashHandoff(handoff);
                window.location.href = '/rrsp';
              }}
              style={{ padding: '8px 16px' }}
            >
              Test these balances in the Retirement Planner →
            </button>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
              Carries your balances, contributions, spending and the age your income stops. You can
              check everything it worked out before it calculates anything.
            </div>
          </div>

          {insights.length > 0 && (() => {
            const hidden = insights.filter(i => i.dismissed);
            const shown = showDismissedInsights ? insights : insights.filter(i => !i.dismissed);
            const setDismissed = (ids: string[]) => {
              settings.dismissedInsights = ids;
              TylerState.forecast.isDirty = true;
              updateReact();
            };
            return (
              <div style={{ marginBottom: '2rem' }}>
                {shown.map(i => (
                  <div key={i.id} style={{
                    padding: '0.7rem 0.9rem', marginBottom: '6px', borderRadius: '8px',
                    background: i.dismissed ? '#f1f5f9' : i.level === 'check' ? '#fef3c7' : '#ecfdf5',
                    borderLeft: `4px solid ${i.dismissed ? '#94a3b8' : i.level === 'check' ? '#d97706' : '#059669'}`,
                    display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start',
                  }}>
                    <div>
                      <strong style={{ color: i.dismissed ? '#64748b' : i.level === 'check' ? '#92400e' : '#065f46' }}>{i.title}</strong>
                      <div style={{ fontSize: '0.85rem', color: '#475569', marginTop: '2px', lineHeight: 1.45 }}>{i.detail}</div>
                    </div>
                    <button
                      className="btn-secondary"
                      style={{ padding: '3px 10px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                      onClick={() => setDismissed(i.dismissed
                        ? (settings.dismissedInsights || []).filter(id => id !== i.id)
                        : [...(settings.dismissedInsights || []), i.id])}
                    >
                      {i.dismissed ? 'Show again' : 'Seen'}
                    </button>
                  </div>
                ))}
                {hidden.length > 0 && (
                  <button
                    className="btn-secondary"
                    style={{ padding: '3px 10px', fontSize: '0.75rem' }}
                    onClick={() => setShowDismissedInsights(!showDismissedInsights)}
                  >
                    {showDismissedInsights ? 'Hide' : `Show ${hidden.length} marked seen`}
                  </button>
                )}
              </div>
            );
          })()}
          <div className="summary-cards" style={{ marginBottom: '2rem' }}>
            <div className="summary-card positive">
              <div className="card-label">{hasHistory ? 'Net Worth (Latest Actual)' : 'Net Worth (Now)'}</div>
              <div className="card-value">{fmt(deflate(first.netWorth, nowIdx))}</div>
            </div>
            <div className={`summary-card ${deflate(last.netWorth, lastIdx) >= deflate(first.netWorth, nowIdx) ? 'positive' : 'negative'}`}>
              <div className="card-label">Net Worth ({monthLabel(last.month)})</div>
              <div className="card-value">{fmt(deflate(last.netWorth, lastIdx))}</div>
            </div>
            {last.netWorthAfterTax !== last.netWorth && (
              <div className="summary-card">
                <div className="card-label">Net Worth after tax ({monthLabel(last.month)})</div>
                <div className="card-value">{fmt(deflate(last.netWorthAfterTax, lastIdx))}</div>
              </div>
            )}
            <div className={`summary-card ${projections[firstForecastIdx].netCashFlow >= 0 ? 'positive' : 'negative'}`}>
              <div className="card-label">Monthly Cash Flow</div>
              <div className="card-value">{fmt(deflate(projections[firstForecastIdx].netCashFlow, firstForecastIdx))}</div>
            </div>
            <div className="summary-card">
              {/* Dated like its neighbours: this is the last month of the plan, not today. */}
              <div className="card-label">Passive Income ({monthLabel(last.month)})</div>
              <div className="card-value">{fmt(deflate(last.passiveIncome * 12, lastIdx))}</div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                a year, covering {(last.passiveIncomeRatio * 100).toFixed(0)}% of your spending
                {' · '}now {fmt(deflate(projections[firstForecastIdx].passiveIncome * 12, firstForecastIdx))}
                {' '}({(projections[firstForecastIdx].passiveIncomeRatio * 100).toFixed(0)}%)
              </div>
            </div>
          </div>

          {settings.showFX && (
            <div style={{ marginBottom: '2rem' }}>
              <h3>By Currency ({monthLabel(last.month)})</h3>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                {CURRENCIES.filter(c => last.byCurrency[c].totalAssets > 0 || last.byCurrency[c].totalLiabilities > 0).map(c => (
                  <div key={c} style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', minWidth: '160px' }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>{c}</div>
                    <div style={{ color: '#059669' }}>Assets: {fmt(deflate(last.byCurrency[c].totalAssets, lastIdx), c)}</div>
                    <div style={{ color: '#dc2626' }}>Liabilities: {fmt(deflate(last.byCurrency[c].totalLiabilities, lastIdx), c)}</div>
                    <div style={{ fontWeight: 600 }}>Net: {fmt(deflate(last.byCurrency[c].netWorth, lastIdx), c)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {last.warnings.length > 0 && (
            <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '8px', marginBottom: '1rem', color: '#92400e' }}>
              {last.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}

          <h3>Net Worth Over Time</h3>
          <div className="frozen-table-wrap">
            <table className="accounts-table compact frozen-table">
              {headerRow(period === 'month' ? 'Month' : 'Year')}
              <tbody>
                <tr><td>Assets</td>{columns.map(c => <React.Fragment key={c.key}>{cell(deflate(totalsAt(c).totalAssets, c.index), { color: '#059669' })}</React.Fragment>)}</tr>
                <tr><td>Liabilities</td>{columns.map(c => <React.Fragment key={c.key}>{cell(deflate(totalsAt(c).totalLiabilities, c.index), { color: '#dc2626' })}</React.Fragment>)}</tr>
                <tr className="total-row"><td style={{ fontWeight: 700 }}>Net Worth</td>{columns.map(c => <React.Fragment key={c.key}>{cell(deflate(totalsAt(c).netWorth, c.index), { bold: true })}</React.Fragment>)}</tr>
                <tr>
                  <td>Change</td>
                  {columns.map((c, i) => {
                    const prev = i > 0 ? deflate(totalsAt(columns[i - 1]).netWorth, columns[i - 1].index) : undefined;
                    const change = prev === undefined ? undefined : deflate(totalsAt(c).netWorth, c.index) - prev;
                    return <React.Fragment key={c.key}>{cell(change, { color: change !== undefined && change < 0 ? '#dc2626' : '#059669' })}</React.Fragment>;
                  })}
                </tr>
                <tr><td>Cash in</td>{columns.map(c => <React.Fragment key={c.key}>{cell(deflate(totalsAt(c).totalInflows, c.index), { color: hueOf(totalsAt(c).totalInflows), dash: true })}</React.Fragment>)}</tr>
                <tr><td>Cash out</td>{columns.map(c => <React.Fragment key={c.key}>{cell(deflate(totalsAt(c).totalOutflows, c.index), { color: hueOf(-totalsAt(c).totalOutflows), dash: true })}</React.Fragment>)}</tr>
                <tr className="total-row"><td style={{ fontWeight: 700 }}>Net cash flow</td>{columns.map(c => <React.Fragment key={c.key}>{cell(deflate(totalsAt(c).netCashFlow, c.index), { bold: true, color: totalsAt(c).netCashFlow >= 0 ? '#059669' : '#dc2626', dash: true })}</React.Fragment>)}</tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cash flow: one net line per category, then a schedule for any category with detail */}
      {view === 'cashFlow' && (() => {
        const sum = (rec: Record<string, number>) => Object.values(rec).reduce((a, b) => a + b, 0);
        const anyIn = (rec: Record<string, number>) => columns.some(c => Math.abs(rec[c.key] || 0) > 0.005);
        const line = (label: string, rec: Record<string, number>, color?: string, bold = false, indent = false) => (
          <tr className={bold ? 'total-row' : undefined}>
            <td title={label} style={{ paddingLeft: indent ? '1.5rem' : undefined, fontWeight: bold ? 700 : undefined }}>{label}</td>
            {columns.map(c => <React.Fragment key={c.key}>{cell(deflate(rec[c.key] || 0, c.index), { color, bold, dash: true })}</React.Fragment>)}
            {cell(sum(rec), { color, bold: true, dash: true })}
          </tr>
        );
        // Colour by the sign of each figure, never by what the item calls itself. An item declared
        // as income can still cost you in a given month - tax that refunds on a loss is the case
        // that exposed this - and a green minus sign is simply wrong.
        const signedLine = (
          label: string, rec: Record<string, number>, opts: { bold?: boolean; indent?: boolean } = {}
        ) => (
          <tr key={label} className={opts.bold ? 'total-row' : undefined}>
            <td title={label} style={{ paddingLeft: opts.indent ? '1.5rem' : undefined, fontWeight: opts.bold ? 700 : undefined }}>{label}</td>
            {columns.map(c => {
              const v = rec[c.key] || 0;
              return <React.Fragment key={c.key}>{cell(deflate(v, c.index), { dash: true, bold: opts.bold, color: hueOf(v) })}</React.Fragment>;
            })}
            {cell(sum(rec), { bold: true, dash: true, color: hueOf(sum(rec)) })}
          </tr>
        );
        const cashRows = flowTable.rows.filter(r => anyIn(r.netWorth));
        // A running total across the columns as shown, so the last column answers "how much has
        // this cost me, or made me, so far".
        const cumulative = (rec: Record<string, number>) => {
          let running = 0;
          return Object.fromEntries(columns.map(c => {
            running += rec[c.key] || 0;
            return [c.key, running];
          }));
        };
        // A schedule is only worth a table where a category has more than one line to show.
        const detailed = flowTable.rows.filter(r => r.items.length > 1);
        return (
          <div>
            <h3>Cash Flow</h3>
            <p className="help-text" style={{ marginTop: 0 }}>
              One line per category, netted: green where more came in than went out, red where it
              didn't. Every category works the same way, including ones you added yourself - so a
              rental's rent, costs, interest and principal total together. Money into a retirement
              account counts the same as money into your bank; moving it between your own accounts
              cancels out, because you are no richer for it. Recorded history months carry no flows,
              so they show as dashes. Converted to {settings.baseCurrency}.
            </p>
            {flowTable.rows.length === 0 ? (
              <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>No cash flows yet. Add income and spending on the Cash Flows tab.</p>
            ) : (
              <div className="frozen-table-wrap">
                <table className="accounts-table compact frozen-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      {columns.map(c => (
                        <th key={c.key} data-period={c.key} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {c.label}
                          {ageIn(c.key) !== null && <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#94a3b8' }}>age {ageIn(c.key)}</div>}
                          {hasHistory && <div style={{ fontSize: '0.65rem', fontWeight: 500, textTransform: 'uppercase', color: sourceColor(c.source) }}>{c.source === 'mixed' ? 'part actual' : c.source}</div>}
                        </th>
                      ))}
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashRows.map(r => signedLine(r.category, r.netWorth))}
                    {signedLine('Total', flowTable.netTotal, { bold: true })}
                    {signedLine('Net cash flow', flowTable.cashNet, { bold: true })}
                    {signedLine(`Cumulative ${bankLabel.replace(/^To /, 'into ')}`, cumulative(flowTable.cashNet), { indent: true })}
                  </tbody>
                </table>
              </div>
            )}

            {/* A schedule per category that has detail worth seeing. */}
            {detailed.map(r => (
              <div key={r.category} style={{ marginTop: '2rem' }}>
                <h3>{r.category}</h3>
                <p className="help-text" style={{ marginTop: 0 }}>
                  Every item in {r.category}, adding up to the Total. Transfers are the part that
                  only moved between accounts you own - a mortgage payment takes the cash but hands
                  you the equity - so taking them off the Total leaves what actually reached your
                  bank, and Cumulative runs that along to date.
                </p>
                <div className="frozen-table-wrap">
                  <table className="accounts-table compact frozen-table">
                    <thead>
                      <tr>
                        <th>Line</th>
                        {columns.map(c => (
                          <th key={c.key} data-period={c.key} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{c.label}</th>
                        ))}
                        <th style={{ textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.items.map(it => signedLine(it.label, it.byPeriod))}
                      {signedLine('Total', r.netWorth, { bold: true })}
                      {/* Transfers are neither a gain nor a loss - the money only changed pocket -
                          so this one stays uncoloured rather than reading as income. */}
                      {anyIn(r.transfersOut) && line('Transfers', r.transfersOut, undefined, false, true)}
                      {signedLine('Net cash flow', r.netCash, { bold: true })}
                      {signedLine('Cumulative', cumulative(r.netCash), { indent: true })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Balance sheet: accounts down the side, periods across */}
      {view === 'balanceSheet' && (() => {
        // An account that reads zero in every period on screen is noise - a dead House3 pushing
        // the real balances down the page. Hidden here rather than deleted, because it is still
        // the user's account and may carry history outside the range being shown.
        const everHasValue = (a: typeof accounts[number]) =>
          columns.some(c => Math.abs(accountAt(c, a.id)?.closingBalance ?? 0) > 0.005);
        const assets = accounts.filter(a => a.side === 'asset' && everHasValue(a));
        const liabilities = accounts.filter(a => a.side === 'liability' && everHasValue(a));
        const hiddenCount = accounts.length - assets.length - liabilities.length;
        const rateFor = (a: typeof accounts[number]) =>
          showLocalCurrency ? 1 : getFXRate(a.currency, settings.baseCurrency, fxRates);
        const accountLine = (a: typeof accounts[number]) => (
          <tr key={a.id}>
            <td title={a.name} style={{ paddingLeft: '1.5rem', cursor: 'pointer' }} onClick={() => { setSelectedAccountId(a.id); setView('accountDetail'); }}>
              {a.name || '(unnamed)'}
              {showLocalCurrency && <span style={{ color: '#94a3b8', marginLeft: '6px', fontSize: '0.75rem' }}>{a.currency}</span>}
            </td>
            {columns.map(c => {
              const ap = accountAt(c, a.id);
              const v = ap ? deflate(ap.closingBalance * rateFor(a), c.index) : undefined;
              // The same rule as everywhere else, with a debt's sign flipped first: what you own
              // reads green and what you owe reads red, and either goes the other way when it
              // turns - an overdrawn account, or a mortgage that has been paid off.
              return <React.Fragment key={c.key}>{cell(v, {
                currency: showLocalCurrency ? a.currency : settings.baseCurrency,
                color: v === undefined ? undefined : hueOf(a.side === 'liability' ? -v : v),
              })}</React.Fragment>;
            })}
          </tr>
        );
        return (
          <div>
            <h3>Balance Sheet</h3>
            <p className="help-text" style={{ marginTop: 0 }}>
              {period === 'year' ? 'Year-end balances (December, or the last month in the timeline).' : 'Month-end balances.'}{' '}
              {showLocalCurrency
                ? `Each account is shown in the currency it is held in, so a column mixes currencies and will not add up; totals are in ${settings.baseCurrency}.`
                : `Everything is converted to ${settings.baseCurrency}, so a column adds up to its total.`}{' '}
              Click an account for its detail.
              {hiddenCount > 0 && ` ${hiddenCount} account${hiddenCount === 1 ? '' : 's'} with no balance in any period ${hiddenCount === 1 ? 'is' : 'are'} hidden.`}
            </p>
            <div className="frozen-table-wrap">
              <table className="accounts-table compact frozen-table">
                {headerRow('Account')}
                <tbody>
                  {sectionRow('Assets', '#059669')}
                  {assets.map(accountLine)}
                  <tr className="total-row"><td style={{ fontWeight: 700 }}>Total assets</td>{columns.map(c => <React.Fragment key={c.key}>{cell(deflate(totalsAt(c).totalAssets, c.index), { bold: true, color: '#059669' })}</React.Fragment>)}</tr>
                  {sectionRow('Liabilities', '#dc2626')}
                  {liabilities.map(accountLine)}
                  <tr className="total-row"><td style={{ fontWeight: 700 }}>Total liabilities</td>{columns.map(c => <React.Fragment key={c.key}>{cell(deflate(totalsAt(c).totalLiabilities, c.index), { bold: true, color: '#dc2626' })}</React.Fragment>)}</tr>
                  <tr className="total-row"><td style={{ fontWeight: 700 }}>Net worth</td>{columns.map(c => {
                    const v = deflate(totalsAt(c).netWorth, c.index);
                    return <React.Fragment key={c.key}>{cell(v, { bold: true, color: hueOf(v) })}</React.Fragment>;
                  })}</tr>
                  {columns.some(c => totalsAt(c).netWorthAfterTax !== totalsAt(c).netWorth) && (
                    <tr>
                      <td title="Every balance you marked as taxed on withdrawal, cut to its after-tax share. Debts come off in full." style={{ paddingLeft: '1.5rem' }}>after tax</td>
                      {columns.map(c => {
                        const v = deflate(totalsAt(c).netWorthAfterTax, c.index);
                        return <React.Fragment key={c.key}>{cell(v, { color: hueOf(v) })}</React.Fragment>;
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Income statement: net cash flow, net investing and growth by account, reconciling to the change in net worth */}
      {view === 'incomeStatement' && (() => {
        // undefined (only ever the very first period, with nothing before it to compare against)
        // passes straight through to a dash, same as every other "not available here" cell.
        const row = (label: string, byPeriod: Record<string, number | undefined>, opts: { color?: string; bold?: boolean; dash?: boolean; indent?: boolean } = {}) => (
          <tr className={opts.bold ? 'total-row' : undefined}>
            <td title={label} style={{ fontWeight: opts.bold ? 700 : undefined, paddingLeft: opts.indent ? '1.5rem' : undefined }}>{label}</td>
            {columns.map(c => {
              const v = byPeriod[c.key];
              return <React.Fragment key={c.key}>{cell(
                v === undefined ? undefined : deflate(v, c.index),
                { ...opts, color: v === undefined ? opts.color : hueOf(v) },
              )}</React.Fragment>;
            })}
          </tr>
        );
        // Active is what you earned and kept: a pure transfer moves cash one way and savings the
        // other, so it cancels here and only real income survives.
        const active = Object.fromEntries(columns.map(c =>
          [c.key, (incomeStatement.netCashFlow[c.key] ?? 0) + (incomeStatement.netInvesting[c.key] ?? 0)]
        ));
        const passive = Object.fromEntries(columns.map(c =>
          [c.key, incomeStatement.passiveRows.reduce((sum, r) => sum + (r.byPeriod[c.key] ?? 0), 0)
            + (incomeStatement.otherReconciling[c.key] ?? 0)]
        ));
        const totalIncome = Object.fromEntries(columns.map(c => [c.key, active[c.key] + passive[c.key]]));
        return (
          <div>
            <h3>Income Statement</h3>
            <p className="help-text" style={{ marginTop: 0 }}>
              Every dollar that moved net worth this {period === 'year' ? 'year' : 'month'}. Active is
              what you earned and kept - cash, plus what went into savings, retirement or debt
              paydown; moving money between your own accounts cancels out and isn't income. Passive is
              what your accounts earned on their own: interest, growth, and revaluation. Total income
              equals the change in net worth. <strong>Other - Reconciling</strong> is a recorded
              balance that stepped with nothing behind it - a big number there means the data needs a
              look, not that you got richer.
            </p>
            <div className="frozen-table-wrap">
              <table className="accounts-table compact frozen-table">
                {headerRow('Line')}
                <tbody>
                  {sectionRow('Active income')}
                  {row('Net cash flow', incomeStatement.netCashFlow, { indent: true })}
                  {row('Net investing (non-cash)', incomeStatement.netInvesting, { indent: true })}
                  {row('Total active income', active, { bold: true })}
                  {sectionRow('Passive income')}
                  {incomeStatement.passiveRows.map(r => (
                    <tr key={r.label}><td title={r.label} style={{ paddingLeft: '1.5rem' }}>{r.label}</td>
                      {columns.map(c => {
                        const v = r.byPeriod[c.key] ?? 0;
                        return <React.Fragment key={c.key}>{cell(deflate(v, c.index), { dash: true, color: hueOf(v) })}</React.Fragment>;
                      })}
                    </tr>
                  ))}
                  {row('Other - Reconciling', incomeStatement.otherReconciling, { indent: true, dash: true })}
                  {row('Total passive income', passive, { bold: true })}
                  {row('Total income', totalIncome, { bold: true })}
                  {row('Change in net worth', incomeStatement.changeInNetWorth, { bold: true })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Account detail: one account, its movements across periods */}
      {view === 'accountDetail' && (() => {
        const acctId = selectedAccountId || accounts[0]?.id;
        const acct = accounts.find(a => a.id === acctId);
        if (!acct) return null;
        // `signed` says which way the row normally points (+1 money in, -1 money out); the colour
        // still comes from the figure, so a negative inflow reads red rather than green.
        const row = (
          label: string,
          pick: (ap: MonthlyAccountProjection) => number,
          opts: { color?: string; bold?: boolean; dash?: boolean; signed?: 1 | -1 } = {},
        ) => (
          <tr className={opts.bold ? 'total-row' : undefined}>
            <td title={label} style={{ fontWeight: opts.bold ? 700 : undefined }}>{label}</td>
            {columns.map(c => {
              const ap = accountAt(c, acct.id);
              const v = ap ? pick(ap) : undefined;
              const color = opts.signed && v !== undefined ? hueOf(v * opts.signed) : opts.color;
              return <React.Fragment key={c.key}>{cell(v === undefined ? undefined : deflate(v, c.index), { ...opts, color, currency: acct.currency })}</React.Fragment>;
            })}
          </tr>
        );
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
              <label>Account:</label>
              <select
                value={acct.id}
                onChange={e => setSelectedAccountId(e.target.value)}
                style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
              >
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name || '(unnamed)'}</option>)}
              </select>
              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{acct.currency} · {acct.bucket} · {acct.side}</span>
            </div>
            <div className="frozen-table-wrap">
              <table className="accounts-table compact frozen-table">
                {headerRow(period === 'month' ? 'Month' : 'Year')}
                <tbody>
                  {row('Opening', ap => ap.openingBalance)}
                  {row('Inflows', ap => ap.inflows, { dash: true, signed: 1 })}
                  {row('Outflows', ap => ap.outflows, { dash: true, signed: -1 })}
                  {row('Interest / growth', ap => ap.interest + ap.growth, { dash: true })}
                  <tr>
                    <td title="Everything the balance moved by that isn't a flow, interest or the growth rate: a fixed monthly change (house revaluation, a mortgage's scheduled paydown, a car depreciating) and any debt-floor clamp.">Other</td>
                    {columns.map(c => {
                      const ap = accountAt(c, acct.id);
                      return <React.Fragment key={c.key}>{cell(ap ? deflate(otherBalanceChange(ap), c.index) : undefined, { dash: true, currency: acct.currency })}</React.Fragment>;
                    })}
                  </tr>
                  {row('Closing', ap => ap.closingBalance, { bold: true })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ForecastResults;
